import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { token, type } from '../theme/tokens.js';

/**
 * Badge tones, matching the upstream adherence contract
 * (`design/design-system/_adherence.oxlintrc.json`):
 * `alert | update | info | neutral | amber`.
 */
export type BadgeTone = 'alert' | 'update' | 'info' | 'neutral' | 'amber';

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  /** Optional leading icon. Lucide only — the design system's single icon set. */
  icon?: ReactNode;
  className?: string;
}

/**
 * Tints are built by layering a low-opacity accent over navy, never by mixing
 * toward white — see the readme's Color note. `tint()` composes from `token()`
 * so no raw colour appears in this file.
 */
const tint = (name: Parameters<typeof token>[0], percent: number) =>
  `color-mix(in srgb, ${token(name)} ${String(percent)}%, transparent)`;

const TONE: Record<BadgeTone, { fg: string; bg: string; border: string }> = {
  alert: {
    fg: token('bp-danger'),
    bg: tint('bp-danger', 12),
    border: tint('bp-danger', 35),
  },
  update: {
    fg: token('bp-success'),
    bg: tint('bp-success', 12),
    border: tint('bp-success', 35),
  },
  info: {
    fg: token('bp-blue-text'),
    bg: tint('bp-blue', 12),
    border: tint('bp-blue', 35),
  },
  amber: {
    fg: token('bp-amber'),
    bg: tint('bp-amber', 12),
    border: tint('bp-amber', 35),
  },
  neutral: {
    fg: token('bp-text-muted'),
    bg: token('bp-border-soft'),
    border: token('bp-border'),
  },
};

/**
 * Small status chip.
 *
 * Used for the CJI-cleared badge, space direction, space status, and file kind
 * (`SPEC.md` FIL-1, FIL-4). Copy arrives as children — the component owns no
 * strings.
 */
export function Badge({ children, tone = 'neutral', icon, className }: BadgeProps) {
  const { fg, bg, border } = TONE[tone];

  return (
    <span
      // py-0.5 is 2px on Tailwind's scale — on-system, no raw px.
      className={cn('inline-flex items-center gap-1.5 whitespace-nowrap border py-0.5', className)}
      style={{
        // Inline, not a utility class — see the note in Eyebrow.
        textTransform: 'uppercase',
        color: fg,
        background: bg,
        borderColor: border,
        borderRadius: token('radius-md'),
        paddingInline: token('space-sm'),
        fontSize: type.size['2xs'],
        fontWeight: type.weight.semibold,
        letterSpacing: type.tracking.label,
      }}
    >
      {icon ? (
        <span aria-hidden="true" className="inline-flex shrink-0 items-center">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}
